// Bulk import into the restaurant's customer book: pasted CSV rows become
// one job the outbox runs; the page polls the job until it settles and
// shows what it imported and skipped. Phone is the identity — a row whose
// phone is already on file merges into that customer.
import { useEffect, useMemo, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { api } from '../../lib/api';
import { useScope } from '../../scope';
import { messageOf, type ImportJob, type ImportRow } from './types';

/** Settled: the outbox will not touch the job again. */
const SETTLED = ['completed', 'dead'];
const POLL_MS = 1000;

/** "name,phone[,email]" lines → import rows; blank lines, a header line and rows without a phone dropped. */
function parseRows(text: string): ImportRow[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split(',').map((f) => f.trim()))
    .filter(([, phone]) => !!phone && /\d/.test(phone))
    .map(([name, phone, email]) => ({ name: name ?? '', phone, email: email || null }));
}

const chipOf = (status: string) => (status === 'completed' ? 'completed' : status === 'dead' ? 'cancelled' : 'pending');

export function Import() {
  const { rid } = useScope();
  const R = `/restaurants/${rid}`;
  const [text, setText] = useState('');
  const [job, setJob] = useState<ImportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => parseRows(text), [text]);
  const running = job !== null && !SETTLED.includes(job.status);

  useEffect(() => {
    if (!job || SETTLED.includes(job.status)) return;
    const t = setTimeout(() => {
      api
        .get<ImportJob>(`${R}/customers/imports/${job.jobId}`)
        .then((got) => setJob((cur) => (cur && cur.jobId === got.jobId ? got : cur)))
        .catch((e) => setError(messageOf(e, 'Polling failed.')));
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [job, R]);

  async function start() {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      setJob(await api.post<ImportJob>(`${R}/customers/imports`, { rows }));
      setError(null);
    } catch (e) {
      setError(messageOf(e, 'Import failed to start.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Customers · Import</div>
          <h1>Import customers</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, alignItems: 'start' }}>
        <div className="panel" data-section="rows">
          <h2>Rows</h2>
          <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '8px 0' }}>
            One customer per line as <span className="money">name,phone,email</span> — the email is optional.
            Phone is the identity: a phone already on file merges into that customer, keeping their name.
          </div>
          <textarea
            className="input"
            aria-label="Import rows"
            rows={12}
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
            placeholder={'Imp One,0412000201\nImp Two,0412000202,two@example.com'}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 14 }}>
            <button className="btn btn-primary" disabled={busy || running || rows.length === 0} onClick={() => void start()}>
              <UploadCloud size={16} />
              {busy ? 'Starting…' : `Import ${rows.length} customer${rows.length === 1 ? '' : 's'}`}
            </button>
            {rows.length === 0 && text.trim() && (
              <span style={{ color: 'var(--warn)', fontSize: 13 }}>No row has a phone yet.</span>
            )}
          </div>
        </div>

        <div className="panel" data-section="job" data-status={job?.status}>
          <h2>Job</h2>
          {!job && <div className="loading">No import started on this page yet.</div>}
          {job && (
            <>
              <div className="rowline">
                <div className="grow">
                  <b className="money">{job.jobId}</b>
                  <span className="sub">{job.rowCount} row{job.rowCount === 1 ? '' : 's'}</span>
                </div>
                <span className={`chip chip-${chipOf(job.status)}`}>{job.status}</span>
              </div>
              {running && <div className="loading">The outbox is running this job…</div>}
              {job.status === 'failed' && job.lastError && (
                <div className="err" role="alert">Attempt failed, retrying — {job.lastError}</div>
              )}
              {job.status === 'dead' && (
                <div className="err" role="alert">The job gave up after retries{job.lastError ? ` — ${job.lastError}` : ''}.</div>
              )}
              {job.status === 'completed' && (
                <>
                  <div className="stats" style={{ margin: '12px 0' }}>
                    <div className="stat">
                      <div className="overline">Imported</div>
                      <div className="v money" data-stat="imported">{job.imported ?? 0}</div>
                    </div>
                    <div className="stat">
                      <div className="overline">Skipped</div>
                      <div className="v money" data-stat="skipped">{job.skipped ?? 0}</div>
                    </div>
                  </div>
                  <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>
                    Imported counts rows merged into an existing customer too; skipped rows had a phone that is not a phone.
                    The customers are searchable in the directory now.
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
